import React, { useContext, useState, useEffect } from "react";
import { useIntl, injectIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { FlitServiceContext } from "../../services/flitService";
import Moment from 'react-moment';
import { makeStyles, withStyles } from "@material-ui/core/styles";
import { Card, CardHeaderToolbar, CardBody, CardHeader } from "../../../_metronic/_partials/controls/Card";
import { useSubheader } from "../../../_metronic/layout/_core/MetronicSubheader";
import {
  Table, TableHead, TableRow, TableCell, TableBody,
  TableSortLabel, TablePagination, Switch, FormControlLabel, LinearProgress,
} from "@material-ui/core";
import { Form, Row, Col, Button } from "react-bootstrap";
import cf from '../../services/codeformatter';
import { ROLES, AIR_EXPRESS_STATUS, AIR_EXPRESS_STATUS_SEQUENCE, MEASUREMENT_SYSTEM_TYPE } from "../../services/flitEnum";
import { getAirExpressStatusCssClasses } from './AirExpressUIHelper';
import { getDeliveryObjectivePillCssClasses, getDeliveryObjectivePillStyle } from '../Consol/ConsolUIHelper';
import cargoImage from "../../assets/images/cargo.png";

const StyledTableCell = withStyles((theme) => ({
  body: {
    fontSize: 13,
  },
}))(TableCell);

const headRows = [
  { id: 'status', align: 'center', disablePadding: false, label: "STATUS", sortable: true },
  { id: 'deliveryObjective', align: 'center', disablePadding: false, label: "DELIVERY_OBJECTIVE", sortable: false },
  { id: 'businessEntity', align: 'center', disablePadding: false, label: "BUSINESS_ENTITY", sortable: false, operatorOnly: true },
  { id: 'submittedOn', align: 'left', disablePadding: false, label: "SUBMITTED_ON", sortable: false },
  { id: 'serviceType', align: 'center', disablePadding: false, label: "SERVICE_TYPE", sortable: true },
  { id: 'airWaybillNo', align: 'center', disablePadding: false, label: "AIR_WAYBILL_NO", sortable: true },
  { id: 'carrier', align: 'center', disablePadding: false, label: "CARRIER", sortable: false },
  { id: 'estimatedDepartureOn', align: 'left', disablePadding: false, label: "ESTIMATED_DEPARTURE_ON", sortable: true },
  { id: 'estimatedArrivalOn', align: 'left', disablePadding: false, label: "ESTIMATED_ARRIVAL_ON", sortable: true },
  { id: 'origin', align: 'center', disablePadding: false, label: "ORIGIN", sortable: false },
  { id: 'destination', align: 'center', disablePadding: false, label: "DESTINATION", sortable: false },
  { id: 'totalWeight', align: 'left', disablePadding: false, label: "TOTAL_WEIGHT", sortable: false },
  { id: 'chargeableWeight', align: 'left', disablePadding: false, label: "CHARGEABLE_WEIGHT", sortable: false },
];

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 500,
  },
  tableWrapper: {
    overflowX: "auto",
  },
}));

function AirExpressTableHead(props) {
  const intl = useIntl();
  const { user } = useSelector(state => state.auth);
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headRows.filter(row => !row.operatorOnly || user.userRole?.find(r => r.role === ROLES.OPERATOR)).map((row) => (
          <StyledTableCell
            key={row.id}
            align={row.align}
            padding={row.disablePadding ? "none" : "default"}
            sortDirection={orderBy === row.id ? order : false}
          >
            {row.sortable ? (
              <TableSortLabel
                active={orderBy === row.id}
                direction={order}
                onClick={createSortHandler(row.id)}
              >
                {intl.formatMessage({ id: row.label })}
              </TableSortLabel>
            ) : (intl.formatMessage({ id: row.label }))}
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

AirExpressTableHead.propTypes = {
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  rowCount: PropTypes.number.isRequired,
}

function AirExpressList(props) {
  const subheader = useSubheader();
  const { intl, history } = props;
  const { user } = useSelector(state => state.auth);
  const { flitSvc } = useContext(FlitServiceContext);
  const [isLoading, setIsLoading] = useState(false);
  const [airExpressList, setAirExpressList] = useState([]);
  const [dense, setDense] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const isOperator = user.userRole?.find(r => r.role === ROLES.OPERATOR);

  const useQuery = () => new URLSearchParams(useLocation().search);
  let qPage = useQuery().get("page");
  let qRowsPerPage = useQuery().get("rowsPerPage");
  const [filter, setFilter] = useState({
    page: qPage ? +qPage : 0,
    rowsPerPage: qRowsPerPage ? +qRowsPerPage : 5,
    orderBy: useQuery().get("orderBy") ?? "id",
    order: useQuery().get("order") ?? "desc",
    status: useQuery().get("status") ?? "",
    originLocation: useQuery().get("originLocation") ?? "",
    destinationLocation: useQuery().get("destinationLocation") ?? ""
  });

  subheader.setTitle(intl.formatMessage({ id: "MENU.AIR_EXPRESS" }));
  const classes = useStyles();

  function refreshList() {
    setIsLoading(true);
    var airExpressFilters = []
    if (filter.status) {
      airExpressFilters.push({ raw: "status eq '" + filter.status + "'" });
    } else {
      airExpressFilters.push({ raw: "status ne '" + AIR_EXPRESS_STATUS.CANCELLED + "' and status ne '" + AIR_EXPRESS_STATUS.DELETED + "'" });
    }
    if (filter.originLocation) {
      airExpressFilters.push({ raw: "(contains(originLocation/name,'" + filter.originLocation + "'))" });
    }
    if (filter.destinationLocation) {
      airExpressFilters.push({ raw: "(contains(destinationLocation/name,'" + filter.destinationLocation + "'))" });
    }

    flitSvc.searchAirExpress(filter.rowsPerPage, filter.page * filter.rowsPerPage, filter.orderBy, filter.order, flitSvc.makeFilterString(airExpressFilters)).then(({ data }) => {
      setAirExpressList(data.value ?? []);
      setTotalRows(data['@odata.count']);
    }).finally(() => {
      setIsLoading(false);
    });
  }

  useEffect(() => {
    flitSvc.insertUrlParam(filter);
    refreshList();
  }, [filter]);

  //Table functions
  function handleRequestSort(event, property) {
    const isDesc = filter.orderBy === property && filter.order === "desc";
    filter.order = isDesc ? "asc" : "desc";
    filter.orderBy = property;
    setFilter({ ...filter });
  }

  function handleChangePage(event, newPage) {
    filter.page = newPage;
    setFilter({ ...filter });
  }

  function handleChangeRowsPerPage(event) {
    filter.rowsPerPage = +event.target.value;
    filter.page = 0;
    setFilter({ ...filter });
  }

  function handleChangeDense(event) {
    setDense(event.target.checked);
  }

  return (
    <Card>
      <CardHeader title={intl.formatMessage({ id: "AIR_EXPRESS_ORDER_LIST" })}>
        <CardHeaderToolbar>
          {isOperator && <Button variant="secondary" className="mr-3" onClick={() => {
            history.push('/airexpress/orders');
          }}>{intl.formatMessage({ id: "AIR_EXPRESS_ORDER_MANAGEMENT" })}</Button>}
          <Button style={{ float: "right" }} onClick={() => {
            history.push('/airexpress/request');
          }}>{intl.formatMessage({ id: "REQUEST_AIR_EXPRESS" })}</Button>
        </CardHeaderToolbar>
      </CardHeader>
      <CardBody>
        <Row>
          <Col className="d-flex align-items-center flex-wrap">
            <Form.Group controlId="status" className="mr-7">
              <Form.Control as="select" value={filter.status} onChange={e => {
                filter.status = e.target.value;
                filter.page = 0;
                setFilter({ ...filter });
              }}>
                <option value=''> {intl.formatMessage({ id: "ALL_ACTIVE_ORDER" })}</option>
                {AIR_EXPRESS_STATUS_SEQUENCE.map(status => (
                  <option key={status} value={status}>{cf.format('AirExpressStatus', status, intl.locale)}</option>
                ))}
              </Form.Control>
              <Form.Text className="text-muted">
                <b>{intl.formatMessage({ id: "FILTER" })}</b>{" "}
                {intl.formatMessage({ id: "STATUS" })}
              </Form.Text>
            </Form.Group>

            <Form.Group controlId="originLocation" className="mr-7">
              <Form.Control
                type="text"
                defaultValue={filter.originLocation}
                onChange={(e) => {
                  if (
                    e.target.value.length >= 2 ||
                    e.target.value.length === 0
                  ) {
                    filter.page = 0;
                    filter.originLocation = e.target.value;
                    setFilter({ ...filter });
                  }
                }}
              />
              <Form.Text className="text-muted">
                <b>{intl.formatMessage({ id: "FILTER" })}</b>{" "}
                {intl.formatMessage({ id: "ORIGIN" })}
              </Form.Text>
            </Form.Group>
            <Form.Group controlId="destinationLocation" className="mr-7">
              <Form.Control
                type="text"
                defaultValue={filter.destinationLocation}
                onChange={(e) => {
                  if (
                    e.target.value.length >= 2 ||
                    e.target.value.length === 0
                  ) {
                    filter.page = 0;
                    filter.destinationLocation = e.target.value;
                    setFilter({ ...filter });
                  }
                }}
              />
              <Form.Text className="text-muted">
                <b>{intl.formatMessage({ id: "FILTER" })}</b>{" "}
                {intl.formatMessage({ id: "DESTINATION" })}
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        {!isLoading && airExpressList.length === 0 && (
          <Row className="border-top pt-5">
            <Col style={{ textAlign: "center" }}>
              <img
                src={cargoImage}
                alt="CargoImage"
                style={{
                  height: "100px",
                  marginTop: "20px",
                  marginBottom: "10px",
                }}
              />
              <br />
              {intl.formatMessage({ id: "NO_AIR_EXPRESS_ORDER" })}
            </Col>
          </Row>
        )}

        <Row>
          <Col>
            {(isLoading || airExpressList.length != 0) && <div className={classes.tableWrapper}>
              {isLoading && <LinearProgress />}
              <Table
                className={classes.table}
                aria-labelledby="tableTitle"
                size={dense ? 'small' : 'medium'}
              >
                <AirExpressTableHead
                  order={filter.order}
                  orderBy={filter.orderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={airExpressList.length}
                >
                </AirExpressTableHead>
                <TableBody>
                  {airExpressList.map((row) => {
                    const quoteRequest = row.activity?.quoteRequest[0];
                    return (
                      <TableRow hover
                        style={{ "cursor": "pointer" }}
                        onClick={() => history.push('/airexpress/' + row.id)}
                        key={row.id} >
                        <StyledTableCell align="center"><span className={getAirExpressStatusCssClasses(row.status)}>{cf.format('AirExpressStatus', row.status, intl.locale)}</span></StyledTableCell>
                        <StyledTableCell align="center">{row.deliveryObjective && <span className={getDeliveryObjectivePillCssClasses()} style={getDeliveryObjectivePillStyle(row.deliveryObjective)}>{row.deliveryObjective}</span>}</StyledTableCell>
                        {isOperator && <StyledTableCell align="center">{quoteRequest?.submittedBy?.businessEntity?.legalName}</StyledTableCell>}
                        <StyledTableCell align="left">{quoteRequest?.submittedOn ? <Moment format="ll">{quoteRequest.submittedOn}</Moment> : ""}</StyledTableCell>
                        <StyledTableCell align="center">{cf.format('AirExpressServiceType', row.serviceType, intl.locale)}</StyledTableCell>
                        <StyledTableCell align="center">{row.airWaybillNo}</StyledTableCell>
                        <StyledTableCell align="center">{row.carrier}</StyledTableCell>
                        <StyledTableCell align="left">{row.estimatedDepartureOn ? <Moment format="ll">{row.estimatedDepartureOn}</Moment> : ""}</StyledTableCell>
                        <StyledTableCell align="left">{row.estimatedArrivalOn ? <Moment format="ll">{row.estimatedArrivalOn}</Moment> : ""}</StyledTableCell>
                        <StyledTableCell align="center">{row.originLocation?.name ?? quoteRequest?.originLocation?.name}</StyledTableCell>
                        <StyledTableCell align="center">{row.destinationLocation?.name ?? quoteRequest?.destinationLocation?.name}</StyledTableCell>
                        <StyledTableCell align="left">{row.totalWeight == 0 ? (quoteRequest ? quoteRequest.totalWeight + (quoteRequest.measurementSystem == MEASUREMENT_SYSTEM_TYPE.METRIC ? ' kg' : ' lb') : '') : row.totalWeight + (row.measurementSystem == MEASUREMENT_SYSTEM_TYPE.METRIC ? ' kg' : ' lb')}</StyledTableCell>
                        <StyledTableCell align="left">{row.chargeableWeight ? row.chargeableWeight + (row.measurementSystem == MEASUREMENT_SYSTEM_TYPE.METRIC ? ' kg' : ' lb') : ''}</StyledTableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>}
            {!isLoading && <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalRows}
              rowsPerPage={filter.rowsPerPage}
              page={filter.page}
              backIconButtonProps={{
                'aria-label': intl.formatMessage({ id: "PREVIOUS_PAGE" }),
              }}
              nextIconButtonProps={{
                'aria-label': intl.formatMessage({ id: "NEXT_PAGE" }),
              }}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />}
            <FormControlLabel
              control={<Switch checked={dense} onChange={handleChangeDense} />}
              label={intl.formatMessage({ id: "DENSE_PADDING" })}
            />
          </Col>
        </Row>
      </CardBody>
    </Card>
  )
}

export default injectIntl(AirExpressList);
